// File: useSalesReport.js

import { useState, useEffect, useMemo, useCallback } from "react";
import moment from "moment";
import { getDataWithComp, getAlert } from "../../../../utils/SamuiUtils";
import { SUMMARY_FIELDS } from "./Fields";

export default function useSalesReport({
  startDate,
  endDate,
  search,
  sortField,
  sortOrder,
  page,
  perPage,
}) {
  const [rawData, setRawData] = useState([]);
  const [detailData, setDetailData] = useState([]);
  const [detailInfo, setDetailInfo] = useState({
    index: 0,
    code: "",
    name: "",
  });

  useEffect(() => {
    getDataWithComp("API_0701_SO_H", "Order By AR_Code DESC")
      .then((list) => setRawData(list || []))
      .catch((e) => getAlert("FAILED", e.message));
  }, []);

  const filteredByDate = useMemo(
    () =>
      rawData.filter((item) => {
        const d = moment(item.Doc_Date).format("YYYY-MM-DD");
        return d >= startDate && d <= endDate;
      }),
    [rawData, startDate, endDate]
  );

  const grouped = useMemo(() => {
    const map = {};
    filteredByDate.forEach((item) => {
      const code = item.AR_Code;
      if (!map[code]) {
        map[code] = { ...item, purchaseCount: 1 };
        SUMMARY_FIELDS.forEach((f) => {
          if (f.numeric) {
            map[code][f.key] = Number(item[f.key]) || 0;
          }
        });
      } else {
        SUMMARY_FIELDS.forEach((f) => {
          if (f.numeric) {
            map[code][f.key] =
              (map[code][f.key] || 0) + (Number(item[f.key]) || 0);
          }
        });
        map[code].purchaseCount += 1;
      }
    });
    return Object.values(map);
  }, [filteredByDate]);

  
  const searched = useMemo(
    () =>
      grouped.filter((item) =>
        Object.values(item).some((v) =>
          v?.toString().toLowerCase().includes(search.toLowerCase())
        )
      ),
    [grouped, search]
  );

  const sorted = useMemo(() => {
    if (!sortField) return searched;
    return [...searched].sort((a, b) => {
      let va = a[sortField];
      let vb = b[sortField];
      if (typeof va === "string") va = va.toLowerCase();
      if (typeof vb === "string") vb = vb.toLowerCase();
      if (va > vb) return sortOrder === "asc" ? 1 : -1;
      if (va < vb) return sortOrder === "asc" ? -1 : 1;
      return 0;
    });
  }, [searched, sortField, sortOrder]);

  const total = sorted.length;
  const pages = perPage > 0 ? Math.ceil(total / perPage) : 1;
  const current = useMemo(() => {
    if (perPage <= 0) return sorted;
    const start = (page - 1) * perPage;
    return sorted.slice(start, start + perPage);
  }, [sorted, page, perPage]);

  const sums = useMemo(() => {
    const result = {};
    SUMMARY_FIELDS.forEach((f) => {
      if (f.numeric) {
        result[`sum${f.key}`] = sorted.reduce(
          (acc, item) => acc + (Number(item[f.key]) || 0),
          0
        );
      }
    });
    return result;
  }, [sorted]);

  const showDetailByIndex = useCallback(
    (idx) => {
      const cust = current[idx];
      if (!cust) return;
      const filterStr = [
        `AND AR_Code='${cust.AR_Code}'`,
        `AND Doc_Date >= '${startDate}'`,
        `AND Doc_Date <= '${endDate}'`,
        "ORDER BY Doc_Date",
      ].join(" ");
      getDataWithComp("API_0702_SO_D", filterStr)
        .then((list) => {
          setDetailData(list || []);
          console.log("Detail data:", list);
          setDetailInfo({ index: idx, code: cust.AR_Code, name: cust.AR_Name });
        })
        .catch((e) => getAlert("FAILED", e.message));
    },
    [current, startDate, endDate]
  );

  return {
    current,
    total,
    pages,
    sums,
    detailData,
    detailInfo,
    showDetailByIndex,
  };
}
